import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SessionTable = ({ 
  sessions = [], 
  onViewDetails = () => {},
  onExtendTime = () => {},
  onCloseSession = () => {},
  onRemoveParticipant = () => {}
}) => {
  const [sortField, setSortField] = useState('tableNumber');
  const [sortDirection, setSortDirection] = useState('asc');

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success text-success-foreground';
      case 'payment-pending':
        return 'bg-warning text-warning-foreground';
      case 'completed':
        return 'bg-primary text-primary-foreground';
      case 'expired':
        return 'bg-error text-error-foreground';
      case 'disputed':
        return 'bg-destructive text-destructive-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Activa';
      case 'payment-pending':
        return 'Pago Pendiente';
      case 'completed':
        return 'Completada';
      case 'expired':
        return 'Expirada';
      case 'disputed':
        return 'En Disputa';
      default:
        return 'Desconocido';
    }
  };

  const formatDuration = (startTime) => {
    const now = new Date();
    const start = new Date(startTime);
    const diffMinutes = Math.floor((now - start) / (1000 * 60));
    
    if (diffMinutes < 60) {
      return `${diffMinutes}m`;
    }
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedSessions = [...sessions]?.sort((a, b) => {
    let aValue = a?.[sortField];
    let bValue = b?.[sortField];
    
    if (typeof aValue === 'string') {
      aValue = aValue?.toLowerCase();
      bValue = bValue?.toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  const SortButton = ({ field, children }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center space-x-1 text-left font-medium text-foreground hover:text-primary transition-colors"
    >
      <span>{children}</span>
      <Icon 
        name={sortField === field ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
        size={14} 
      />
    </button>
  );

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden">
      <div className="table-scroll-wrapper overflow-x-auto">
        <table className="w-full app-responsive-table">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <SortButton field="tableNumber">Mesa</SortButton>
              </th>
              <th className="px-4 py-3 text-left">
                <SortButton field="participants">Participantes</SortButton>
              </th>
              <th className="px-4 py-3 text-left">
                <SortButton field="totalAmount">Total</SortButton>
              </th>
              <th className="px-4 py-3 text-left">
                <SortButton field="startTime">Duración</SortButton>
              </th>
              <th className="px-4 py-3 text-left">
                <SortButton field="status">Estado</SortButton>
              </th>
              <th className="px-4 py-3 text-left">Progreso</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {sortedSessions?.map((session) => (
              <tr key={session?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-2 flex-wrap">
                    <div className="w-8 h-8 bg-primary text-primary-foreground rounded-lg flex items-center justify-center text-sm font-medium">
                      {session?.tableNumber}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-foreground truncate">{session?.tableName}</div>
                      <div className="text-xs text-text-secondary truncate">ID: {session?.id?.slice(-8)}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-2 flex-wrap min-w-0">
                    <Icon name="Users" size={16} className="text-text-secondary" />
                    <span className="text-foreground">{session?.participants}</span>
                    {session?.activeParticipants < session?.participants && (
                      <span className="text-xs text-warning truncate">
                        ({session?.activeParticipants} activos)
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-mono font-medium text-foreground truncate">
                    {formatAmount(session?.totalAmount)}
                  </div>
                  <div className="text-xs text-text-secondary truncate">
                    {session?.paidAmount > 0 && `${formatAmount(session?.paidAmount)} pagado`}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-foreground truncate">{formatDuration(session?.startTime)}</div>
                  <div className="text-xs text-text-secondary truncate">
                    Iniciada: {new Date(session.startTime)?.toLocaleTimeString('es-ES', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session?.status)}`}>
                    {getStatusLabel(session?.status)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${session?.completionPercentage}%` }}
                    />
                  </div>
                  <div className="text-xs text-text-secondary mt-1 truncate">
                    {session?.completionPercentage}% completado
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end space-x-1 flex-wrap">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetails(session?.id)}
                      iconName="Eye"
                      iconSize={14}
                    >
                      Ver
                    </Button>
                    
                    {session?.status === 'active' && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onExtendTime(session?.id)}
                          iconName="Clock"
                          iconSize={14}
                        >
                          Extender
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onCloseSession(session?.id)}
                          iconName="X"
                          iconSize={14}
                        >
                          Cerrar
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sessions?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Database" size={32} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-text-secondary">No hay sesiones activas</p>
        </div>
      )}
    </div>
  );
};

export default SessionTable;